import { Dispatch, SetStateAction, FC, useEffect, useMemo, memo } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Divider, HStack, Stack, Tag, TagCloseButton, TagLabel } from '@chakra-ui/react'

import { FormItem } from 'components'
import { ActivityType } from 'Activities/types'
import FrequencieActivitiesTypesSelect from './components/FrequencieActivitiesTypesSelect'

type Form = {
  has_activities_types: string[]
  not_has_activities_types: string[]
}

type RemoveFilterParams = {
  key: 'has_activities_types' | 'not_has_activities_types'
  name: string
}

export type Props = {
  activitiesTypes: ActivityType[]
  filter: any
  setFilter: Dispatch<SetStateAction<any>>
  onClose: () => void
}

const FrequencieActivitiesTypesFilterTemplate: FC<Props> = ({ activitiesTypes, filter, setFilter, onClose }) => {
  const { watch, reset, setValue, handleSubmit } = useForm<Form>({
    defaultValues: { has_activities_types: [], not_has_activities_types: [] },
  })

  function onSubmit({ has_activities_types, not_has_activities_types }: any) {
    setFilter({ ...filter, has_activities_types, not_has_activities_types })
    onClose()
  }

  const has_activities_types: string[] = watch('has_activities_types')
  const not_has_activities_types: string[] = watch('not_has_activities_types')

  const availableActivitiesTypes = useMemo(() => {
    return activitiesTypes.filter(({ name }) => {
      return !has_activities_types.includes(name as string) && !not_has_activities_types.includes(name as string)
    })
  }, [activitiesTypes, has_activities_types, not_has_activities_types])

  async function removeActivityType({ key, name }: RemoveFilterParams) {
    const activitiesTypesNames = key === 'has_activities_types' ? has_activities_types : not_has_activities_types
    const newActivitiesTypesNames = activitiesTypesNames.filter((currentName: string) => currentName !== name)
    setValue(key, newActivitiesTypesNames)
  }

  useEffect(() => {
    const { has_activities_types, not_has_activities_types } = filter
    reset({ has_activities_types, not_has_activities_types })
  }, [filter])

  return (
    <Stack as="form" id="frequencia-activities-filter-form" onSubmit={handleSubmit(onSubmit)} spacing={2}>
      <FormItem label="Se possuem estes tipos de atividades">
        {has_activities_types.length > 0 && (
          <HStack flexWrap="wrap" mb={4}>
            {has_activities_types.map((name: string) => {
              return (
                <Tag bg="bgContainer" key={name} variant="filter-tags">
                  <TagLabel>{name}</TagLabel>
                  <TagCloseButton onClick={() => removeActivityType({ key: 'has_activities_types', name })} />
                </Tag>
              )
            })}
          </HStack>
        )}

        <FrequencieActivitiesTypesSelect
          isDisabled={availableActivitiesTypes.length === 0}
          onChange={(value) => setValue('has_activities_types', [...has_activities_types, value])}
          activitiesTypes={availableActivitiesTypes}
        />
      </FormItem>

      <Divider />

      <FormItem label="Se não possuem estes tipos de atividades">
        {not_has_activities_types.length > 0 && (
          <HStack flexWrap="wrap" mb={4}>
            {not_has_activities_types.map((name: string) => {
              return (
                <Tag bg="bgContainer" key={name} variant="filter-tags">
                  <TagLabel>{name}</TagLabel>
                  <TagCloseButton onClick={() => removeActivityType({ key: 'not_has_activities_types', name })} />
                </Tag>
              )
            })}
          </HStack>
        )}

        <FrequencieActivitiesTypesSelect
          isDisabled={availableActivitiesTypes.length === 0}
          onChange={(value) => setValue('not_has_activities_types', [...not_has_activities_types, value])}
          activitiesTypes={availableActivitiesTypes}
        />
      </FormItem>

      <Button form="frequencia-activities-filter-form" type="submit">
        Filtrar
      </Button>
    </Stack>
  )
}

export default memo(FrequencieActivitiesTypesFilterTemplate)
